import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import type { CampaignReport } from '@/types/report';

interface ShareReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: CampaignReport;
}

export const ShareReportDialog = ({ open, onOpenChange, report }: ShareReportDialogProps) => {
  const [message, setMessage] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleShare = async () => {
    if (!report.campaigns?.candidate_email) {
      toast({
        title: "Erreur",
        description: "Email du candidat non disponible.",
        variant: "destructive",
      });
      return;
    }

    setIsSharing(true);
    
    try {
      // ✅ Utiliser la nouvelle fonction generate-report-pdf avec Gotenberg
      const { error } = await supabase.functions.invoke('generate-report-pdf', {
        body: {
          reportId: report.id,
          recruiterMessage: message.trim() || null,
          sendToCandidate: true, // ✅ Paramètre correct pour l'Edge Function
          anonymize: false // ✅ NOUVEAU : PDF complet pour candidats (pas d'anonymisation)
        },
      });

      if (error) throw error;

      // ✅ Invalider le cache pour rafraîchir les compteurs
      queryClient.invalidateQueries({ queryKey: ['required-actions'] });

      toast({
        title: "Rapport partagé",
        description: `Le rapport PDF a été généré et envoyé par email à ${report.campaigns.candidate_name}.`,
      });

      onOpenChange(false);
      setMessage('');
    } catch (error) {
      console.error('Erreur partage rapport:', error);
      toast({
        title: "Erreur",
        description: "Impossible de partager le rapport. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[573px] p-6 bg-white rounded-3xl border-none">
        {/* Accessibilité */}
        <VisuallyHidden>
          <DialogTitle>Partager le rapport par email</DialogTitle>
          <DialogDescription>
            Envoyez le rapport de références par email à une ou plusieurs adresses
          </DialogDescription>
        </VisuallyHidden>
        
        {/* En-tête */}
        <div className="flex flex-col justify-start items-start gap-1">
          <div className="justify-start text-gray-800 text-2xl font-medium font-['Bricolage_Grotesque']">
            Partager le rapport par email
          </div>
          <div className="justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
            Le rapport PDF sera généré et envoyé à <strong>{report.campaigns?.candidate_name}</strong> 
            ({report.campaigns?.candidate_email}) pour le poste de{' '}
            <strong>{report.campaigns?.target_position}</strong>.
          </div>
        </div>

        {/* Champ de message */}
        <div className="self-stretch flex flex-col justify-start items-start gap-4">
          <div className="self-stretch flex flex-col justify-start items-start gap-2">
            <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
              Message personnel (optionnel)
            </div>
            <div className="self-stretch min-h-[100px] px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20">
              <textarea
                placeholder="Ajoutez un message personnel qui sera inclus dans l'email..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="w-full bg-transparent text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] placeholder:text-neutral-400 outline-none resize-none"
              />
            </div>
          </div>
        </div>

        {/* Boutons */}
        <div className="self-stretch inline-flex justify-end items-center gap-3">
          <button 
            onClick={() => onOpenChange(false)}
            className="h-10 p-3 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-start items-center gap-1 overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <div className="justify-center text-neutral-400 text-sm font-semibold font-['Bricolage_Grotesque']">
              Annuler
            </div>
          </button>
          <button 
            onClick={handleShare} 
            disabled={isSharing}
            className="h-10 p-3 bg-teal-400 rounded-[10px] flex justify-start items-center gap-1 overflow-hidden cursor-pointer hover:bg-teal-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="justify-center text-white text-sm font-semibold font-['Bricolage_Grotesque']">
              {isSharing ? "Envoi en cours..." : "Envoyer le rapport"}
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};